// @flow
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import BreakSpliceForm from 'esm-web-pro/containers/pages/network/objects/point12/BreakSpliceForm';
import DisconnectFiberFromODFPortForm
    from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/DisconnectFiberFromODFPortForm';
import DisconnectPortFromODFPortForm
    from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/DisconnectPortFromPortForm';
import type { Splice } from './CableList/Point12CableList';


export const DISCONNECT_FIBER_KEY = 'BREAK_SPLICE';

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    disconnecting: Object,
    splices: Array<Splice>,
    getFiberCableSeq: Function,
    onBreak: Function,
    opticalPorts: Object[],
};

const DisconnectFiber = (props: Props) => {
    const {
        disconnecting, splices, getFiberCableSeq, closePanel, onBreak, opticalPorts,
    } = props;

    if (!disconnecting) return null;

    const handleSave = () => {
        closePanel(DISCONNECT_FIBER_KEY, true);
        onBreak();
    };

    const handleClose = () => {
        closePanel(DISCONNECT_FIBER_KEY);
    };

    if (disconnecting.type === 'splice') {
        const [spliceFirstFiber, spliceSecondFiber] = [...disconnecting.fiber_starts, ...disconnecting.fiber_ends];

        return (
            <SidePane
                id={DISCONNECT_FIBER_KEY}
                openInitially
                sensitive
                key={disconnecting.uuid}
                title={(
                    <FormattedMessage
                        id="break-splice-title"
                        defaultMessage="Broke Splice {spliceSequence} between cable
                        {cable1Sequence} fiber {fiber1Sequence}
                    and cable {cable2Sequence} fiber {fiber2Sequence}"
                        values={{
                            spliceSequence: disconnecting.sequence,
                            cable1Sequence: getFiberCableSeq(spliceFirstFiber.container),
                            fiber1Sequence: spliceFirstFiber.sequence_number,
                            cable2Sequence: getFiberCableSeq(spliceSecondFiber.container),
                            fiber2Sequence: spliceSecondFiber.sequence_number,
                        }}
                    />
                )}
            >
                <BreakSpliceForm
                    splice={disconnecting}
                    splices={splices}
                    onClose={handleClose}
                    onBreak={handleSave}
                />
            </SidePane>
        );
    }

    if (disconnecting.fiberFromPort) {
        return (
            <SidePane
                id={DISCONNECT_FIBER_KEY}
                openInitially
                sensitive
                key={disconnecting.uuid}
                title={(
                    <FormattedMessage
                        id="disconnect-fiber-opticalport-title"
                        defaultMessage="Disconnect cable {cableSequence} fiber {fiberSequence} from port {portRef}"
                        values={{
                            cableSequence: getFiberCableSeq(disconnecting.fiberFromPort.fiber.container),
                            fiberSequence: disconnecting.fiberFromPort.fiber.sequence_number,
                            portRef: disconnecting.fiberFromPort.ref,
                        }}
                    />
                )}
            >
                <DisconnectFiberFromODFPortForm
                    port={disconnecting.fiberFromPort}
                    opticalPorts={opticalPorts}
                    onClose={handleClose}
                    onSave={handleSave}
                />
            </SidePane>
        );
    }

    if (disconnecting.portFromPort) {
        return (
            <SidePane
                id={DISCONNECT_FIBER_KEY}
                openInitially
                sensitive
                key={disconnecting.uuid}
                title={(
                    <FormattedMessage
                        id="disconnect-port-opticalport-title"
                        defaultMessage="Disconnect port from port {ref}"
                        values={{
                            firstPortRef: disconnecting.portFromPort.ref,
                            secondPortRef: disconnecting.portFromPort.port.ref,
                        }}
                    />
                )}
            >
                <DisconnectPortFromODFPortForm
                    port={disconnecting.portFromPort}
                    opticalPorts={opticalPorts}
                    onClose={handleClose}
                    onSave={handleSave}
                />
            </SidePane>
        );
    }

    return null;
};

export default connect(null, (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))(DisconnectFiber);
